// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { useMoveChat } from '@features/moves/hooks/useMoveChat';
import { useSendMessage } from '@features/moves/hooks/useSendMessage';
import { useGetMoveDetailsQuery } from '../queries/getMoveDetails.query.generated';
import { useUpdateSmsMessageMutation } from '../mutations/updateSmsMessage.mutation.generated';
import { markMessagesAsRead, markMoveAsUnread, hasUnreadMessages } from '@features/moves/services/smsReadStatusTracker';
// Global SMS subscription now handled in Layout component
import config from '../../../config.frontend';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';

import { css } from '@emotion/css';
import {
  Theme,
  useTheme,
  Typography,
  Box,
  Chip,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
  Skeleton,
} from '@mui/material';
import { BiMessageDetail, BiSend, BiError, BiEnvelope, BiCheck } from 'react-icons/bi';
import { formatMoveId } from '@services/moveService';
import { isMoveLite } from '@utils/tiers';

// TYPES ---------------------------------------------------------------- //

interface MoveChatInterfaceProps {
  moveId?: number;
  consumerPickup?: boolean;
}

interface MessageImagesProps {
  media: any;
  imageClassName: string;
  imageWrapperClassName: string;
  imageSkeletonClassName: string;
  expandedImageClassName?: string;
  expandedImageContainerClassName?: string;
}

// Component for displaying message images (moved outside to prevent re-creation)
const MessageImages = React.memo(
  ({
    media,
    imageClassName,
    imageWrapperClassName,
    imageSkeletonClassName,
    expandedImageClassName,
    expandedImageContainerClassName,
  }: MessageImagesProps) => {
    const [loadedImages, setLoadedImages] = React.useState<{ [key: string]: boolean }>({});
    const [expandedImage, setExpandedImage] = React.useState<string | null>(null);

    if (!media || !media.mediaUrls || media.mediaUrls.length === 0) return null;

    return (
      <>
        <Box className={imageWrapperClassName}>
          {media.mediaUrls.map((url: string, index: number) => {
            const mediaType = media.mediaTypes?.[index] || 'image';
            const isImage = mediaType.startsWith('image/');

            if (!isImage) return null;

            return (
              <Box key={`${url}-${index}`} style={{ position: 'relative' }}>
                {!loadedImages[url] && <Skeleton variant='rectangular' className={imageSkeletonClassName} />}
                <img
                  src={url}
                  alt={`Message attachment ${index + 1}`}
                  className={imageClassName}
                  style={{ display: loadedImages[url] ? 'block' : 'none' }}
                  onLoad={() => setLoadedImages(prev => ({ ...prev, [url]: true }))}
                  onClick={() => setExpandedImage(url)}
                />
              </Box>
            );
          })}
        </Box>

        {/* Expanded image modal */}
        {expandedImage && (
          <Box className={expandedImageContainerClassName} onClick={() => setExpandedImage(null)}>
            <img src={expandedImage} alt='Expanded view' className={expandedImageClassName} />
            <IconButton
              style={{ position: 'absolute', top: '20px', right: '20px', color: 'white' }}
              onClick={e => {
                e.stopPropagation();
                setExpandedImage(null);
              }}
            >
              ✕
            </IconButton>
          </Box>
        )}
      </>
    );
  }
);

MessageImages.displayName = 'MessageImages';

// COMPONENT ---------------------------------------------------------------- //

const MoveChatInterfaceContent = ({ moveId, consumerPickup }: MoveChatInterfaceProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);
  const [messageInput, setMessageInput] = React.useState('');
  const [visibleMessageIds, setVisibleMessageIds] = React.useState<Set<string>>(new Set());
  const [isUnread, setIsUnread] = React.useState(false);

  // Fetch move details to get consumer phone
  const { data: moveData, loading: moveLoading } = useGetMoveDetailsQuery({
    variables: { moveId: moveId! }, // Changed from 'id' to 'moveId'
    skip: !moveId,
    fetchPolicy: 'cache-and-network',
  });

  const consumerPhone = moveData?.moves_by_pk?.consumer_phone;

  // Fetch SMS messages using shared hook (no polling - streaming handles updates)
  const { messages, loading, error, refetch } = useMoveChat(moveId, consumerPickup);

  // Track read/unread status and listen for changes
  React.useEffect(() => {
    if (!moveId) return;

    // Check initial status
    setIsUnread(hasUnreadMessages(moveId));

    // Listen for localStorage changes (same tab)
    const handleStatusChange = (e: CustomEvent) => {
      if (e.detail?.moveId === moveId) {
        setIsUnread(e.detail.hasUnread);
      }
    };

    // Listen for storage changes (cross-tab)
    const handleStorageChange = () => {
      setIsUnread(hasUnreadMessages(moveId));
    };

    window.addEventListener('sms-read-status-changed', handleStatusChange as EventListener);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('sms-read-status-changed', handleStatusChange as EventListener);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [moveId]);

  // Find the most recent outbound message to get the sender phone
  const lastOutboundMessage = React.useMemo(() => {
    if (!messages || messages.length === 0) return null;

    // Sort by createdat descending and find first outbound message
    const sortedMessages = [...messages].sort((a, b) => {
      const dateA = a.createdat ? new Date(a.createdat).getTime() : 0;
      const dateB = b.createdat ? new Date(b.createdat).getTime() : 0;
      return dateB - dateA; // Descending order (newest first)
    });

    return sortedMessages.find(msg => msg.direction === 'outbound');
  }, [messages]);

  // Use the last outbound message's sender phone, or fallback to Twilio number for first message
  const senderPhone = lastOutboundMessage?.sender_phone || config.twilioNumber;

  // Send message hook
  const { sendMessage, sending } = useSendMessage(moveId, consumerPhone, senderPhone || undefined, {
    onSuccess: () => {
      setMessageInput('');
      // Note: No need to refetch - subscription handles updates
      scrollToBottom();
    },
  });

  // Hold a reload while a typed message is unsent or a send is running.
  useReloadGuard(messageInput.trim() !== '' || sending, 'Chat message');

  // Update message mutation for retry functionality
  const [updateSmsMessage] = useUpdateSmsMessageMutation();

  const formatTime = (date?: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (date?: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatPhoneNumber = (phone?: string | null) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  };

  const getStatusInfo = (status?: string | null, error?: string | null) => {
    if (error || status === 'failed') {
      return { label: 'Not Delivered', color: 'error' as const, showRetry: true };
    }
    if (status === 'delivered' || status === 'sent') {
      return { label: 'Sent', color: 'default' as const, showRetry: false };
    }
    if (status === 'pending' || status === 'queued' || status === 'initiating') {
      return { label: 'Sending...', color: 'warning' as const, showRetry: false };
    }
    return { label: status || 'Unknown', color: 'default' as const, showRetry: false };
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Set up Intersection Observer to track visible messages
  React.useEffect(() => {
    if (!messagesContainerRef.current || messages.length === 0 || !moveId) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const messageId = entry.target.getAttribute('data-message-id');
            if (messageId) {
              setVisibleMessageIds(prev => new Set(prev).add(messageId));
            }
          }
        });
      },
      {
        root: messagesContainerRef.current,
        threshold: 0.5, // Message is considered visible when 50% is shown
      }
    );

    // Observe all message elements
    const messageElements = messagesContainerRef.current.querySelectorAll('[data-message-id]');
    messageElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [messages, moveId]);

  // Mark messages as read when they become visible
  React.useEffect(() => {
    if (visibleMessageIds.size === 0 || !moveId) return;

    // Get the most recent visible message
    const visibleMessagesArray = Array.from(visibleMessageIds);
    const visibleMessages = messages.filter(msg => msg.id && visibleMessagesArray.includes(String(msg.id)));

    if (visibleMessages.length > 0) {
      // Sort to get the most recent message
      const sortedVisible = [...visibleMessages].sort((a, b) => {
        const dateA = new Date(a.createdat || 0).getTime();
        const dateB = new Date(b.createdat || 0).getTime();
        return dateB - dateA;
      });

      const mostRecentVisible = sortedVisible[0];
      if (mostRecentVisible?.id) {
        // Debounce the marking to avoid too many calls
        const timer = setTimeout(() => {
          markMessagesAsRead(moveId, String(mostRecentVisible.id));
        }, 1000); // 1 second delay after viewing

        return () => clearTimeout(timer);
      }
    }
  }, [visibleMessageIds, messages, moveId]);

  React.useEffect(() => {
    if (messages && messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  const handleSendMessage = async () => {
    const trimmedMessage = messageInput.trim();

    if (!trimmedMessage) {
      return;
    }

    if (sending) {
      return;
    }

    try {
      await sendMessage(trimmedMessage);
    } catch (error) {
      // Error handling is done in useSendMessage hook
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle retry for failed messages
  const handleRetryMessage = React.useCallback(
    async (message: any) => {
      if (!message.id) return;

      try {
        await updateSmsMessage({
          variables: {
            id: message.id,
            status: 'initiating',
          },
        });
      } catch (error) {
        // Error will be handled by the mutation
      }
    },
    [updateSmsMessage]
  );

  // Parse message metadata to extract media information
  const parseMessageMetadata = (metadata: any) => {
    if (!metadata) return null;

    try {
      const parsed = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
      if (parsed.numMedia > 0 && parsed.mediaUrls?.length > 0) {
        return {
          numMedia: parsed.numMedia,
          mediaUrls: parsed.mediaUrls,
          mediaTypes: parsed.mediaTypes || [],
        };
      }
    } catch (e) {
      // Silently handle parsing errors
    }
    return null;
  };

  // Determine if message is from dealer or customer based on direction field
  const isFromDealer = (message: any) => {
    // Check if message has direction field
    if (message.direction) {
      return message.direction === 'outbound';
    }
    // Fallback to sender_phone logic for older messages
    const senderPhone = message.sender_phone;
    return !senderPhone || senderPhone.includes('dealer') || senderPhone.length < 10;
  };

  if (error) {
    return (
      <Box className={cls.chatContainer}>
        {/* Move Context Header */}
        {moveData?.moves_by_pk && (
          <Box className={cls.moveContext}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Chip
                label={formatMoveId(moveData.moves_by_pk.id, { isLite: isMoveLite(moveData.moves_by_pk) })}
                size='small'
                color='primary'
                sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }}
              />
              {moveData.moves_by_pk.reference_num && (
                <Typography variant='caption' color='text.secondary'>
                  Ref: {moveData.moves_by_pk.reference_num}
                </Typography>
              )}
            </Box>
            <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '1rem', mb: 0 }}>
              {moveData.moves_by_pk.consumer_name || 'Unknown Customer'}
            </Typography>
            {consumerPhone && (
              <Typography variant='body2' sx={{ color: theme?.palette?.text?.secondary, mb: 0.25 }}>
                {formatPhoneNumber(consumerPhone)}
              </Typography>
            )}
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
              {moveData.moves_by_pk.vehicle_year} {moveData.moves_by_pk.vehicle_make}{' '}
              {moveData.moves_by_pk.vehicle_model}
              {moveData.moves_by_pk.vehicle_vin && ` • VIN: ${moveData.moves_by_pk.vehicle_vin}`}
            </Typography>
          </Box>
        )}

        <Box className={cls.messagesContainer}>
          <Box className={cls.emptyState}>
            <Typography variant='body2' sx={{ color: theme?.palette?.error?.main }}>
              Failed to load messages. Please try again later.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box className={cls.chatContainer}>
        {/* Move Context Header */}
        {moveData?.moves_by_pk && (
          <Box className={cls.moveContext}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Chip
                label={formatMoveId(moveData.moves_by_pk.id, { isLite: isMoveLite(moveData.moves_by_pk) })}
                size='small'
                color='primary'
                sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }}
              />
              {moveData.moves_by_pk.reference_num && (
                <Typography variant='caption' color='text.secondary'>
                  Ref: {moveData.moves_by_pk.reference_num}
                </Typography>
              )}
            </Box>
            <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '1rem', mb: 0 }}>
              {moveData.moves_by_pk.consumer_name || 'Unknown Customer'}
            </Typography>
            {consumerPhone && (
              <Typography variant='body2' sx={{ color: theme?.palette?.text?.secondary, mb: 0.25 }}>
                {formatPhoneNumber(consumerPhone)}
              </Typography>
            )}
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
              {moveData.moves_by_pk.vehicle_year} {moveData.moves_by_pk.vehicle_make}{' '}
              {moveData.moves_by_pk.vehicle_model}
              {moveData.moves_by_pk.vehicle_vin && ` • VIN: ${moveData.moves_by_pk.vehicle_vin}`}
            </Typography>
          </Box>
        )}

        <Box className={cls.messagesContainer}>
          <Typography
            variant='body2'
            sx={{ color: theme?.palette?.text?.secondary, textAlign: 'center', padding: '20px' }}
          >
            Loading messages...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (messages.length === 0 && !loading) {
    return (
      <Box className={cls.chatContainer}>
        {/* Move Context Header */}
        {moveData?.moves_by_pk && (
          <Box className={cls.moveContext}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Chip
                label={formatMoveId(moveData.moves_by_pk.id, { isLite: isMoveLite(moveData.moves_by_pk) })}
                size='small'
                color='primary'
                sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }}
              />
              {moveData.moves_by_pk.reference_num && (
                <Typography variant='caption' color='text.secondary'>
                  Ref: {moveData.moves_by_pk.reference_num}
                </Typography>
              )}
            </Box>
            <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '1rem', mb: 0 }}>
              {moveData.moves_by_pk.consumer_name || 'Unknown Customer'}
            </Typography>
            {consumerPhone && (
              <Typography variant='body2' sx={{ color: theme?.palette?.text?.secondary, mb: 0.25 }}>
                {formatPhoneNumber(consumerPhone)}
              </Typography>
            )}
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
              {moveData.moves_by_pk.vehicle_year} {moveData.moves_by_pk.vehicle_make}{' '}
              {moveData.moves_by_pk.vehicle_model}
              {moveData.moves_by_pk.vehicle_vin && ` • VIN: ${moveData.moves_by_pk.vehicle_vin}`}
            </Typography>
          </Box>
        )}

        <Box className={cls.messagesContainer}>
          <Box className={cls.emptyState}>
            <BiMessageDetail size={48} color={theme?.palette?.text?.disabled} />
            <Typography variant='body2' sx={{ color: theme?.palette?.text?.secondary, marginTop: '12px' }}>
              Send a message to start the conversation
            </Typography>
          </Box>
        </Box>

        {/* Input field for sending first message */}
        <Box className={cls.inputArea}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={messageInput}
            placeholder={!consumerPhone ? 'Customer phone number is missing' : 'Type your message...'}
            onChange={e => setMessageInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending || !consumerPhone}
            size='small'
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                backgroundColor: theme?.palette?.background?.paper,
              },
            }}
          />
          <Tooltip title={!consumerPhone ? 'Customer phone number is required' : 'Send message'}>
            <span>
              <IconButton
                onClick={handleSendMessage}
                disabled={!messageInput.trim() || sending || !consumerPhone}
                color='primary'
                sx={{
                  marginLeft: '8px',
                  backgroundColor: theme?.palette?.primary?.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme?.palette?.primary?.dark,
                  },
                  '&:disabled': {
                    backgroundColor: theme?.palette?.action?.disabledBackground,
                    color: theme?.palette?.action?.disabled,
                  },
                }}
              >
                {sending ? <CircularProgress size={24} color='inherit' /> : <BiSend size={20} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  // Sort messages by date (oldest first for chat view)
  const sortedMessages = [...messages].sort((a, b) => {
    const dateA = a.createdat ? new Date(a.createdat).getTime() : 0;
    const dateB = b.createdat ? new Date(b.createdat).getTime() : 0;
    return dateA - dateB;
  });

  // Group messages by date
  const groupedMessages: { [date: string]: typeof messages } = {};
  sortedMessages.forEach(message => {
    const dateKey = formatDate(message.createdat);
    if (!groupedMessages[dateKey]) {
      groupedMessages[dateKey] = [];
    }
    groupedMessages[dateKey].push(message);
  });

  return (
    <Box className={cls.chatContainer}>
      {/* Move Context Header */}
      {moveData?.moves_by_pk && (
        <Box className={cls.moveContext}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              position: 'relative',
              gap: 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={formatMoveId(moveData.moves_by_pk.id, { isLite: isMoveLite(moveData.moves_by_pk) })}
                  size='small'
                  color='primary'
                  sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }}
                />
                {moveData.moves_by_pk.reference_num && (
                  <Typography variant='caption' color='text.secondary'>
                    Ref: {moveData.moves_by_pk.reference_num}
                  </Typography>
                )}
              </Box>
              <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '1rem', mb: 0 }}>
                {moveData.moves_by_pk.consumer_name || 'Unknown Customer'}
              </Typography>
              {consumerPhone && (
                <Typography variant='body2' sx={{ color: theme?.palette?.text?.secondary, mb: 0.25 }}>
                  {formatPhoneNumber(consumerPhone)}
                </Typography>
              )}
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                {moveData.moves_by_pk.vehicle_year} {moveData.moves_by_pk.vehicle_make}{' '}
                {moveData.moves_by_pk.vehicle_model}
                {moveData.moves_by_pk.vehicle_vin && ` • VIN: ${moveData.moves_by_pk.vehicle_vin}`}
              </Typography>
            </Box>
            {/* Mark as unread/read toggle button */}
            {messages.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  ml: 1,
                  mr: 4,
                  position: 'relative',
                  zIndex: 10,
                }}
              >
                {isUnread ? (
                  <Tooltip title='Mark conversation as read'>
                    <IconButton
                      size='small'
                      onClick={() => {
                        if (moveId && messages.length > 0) {
                          // Use the most recent message ID to mark as read
                          const lastMessage = [...messages].sort((a, b) => {
                            const dateA = new Date(b.createdat || 0).getTime();
                            const dateB = new Date(a.createdat || 0).getTime();
                            return dateA - dateB;
                          })[0];
                          if (lastMessage?.id) {
                            markMessagesAsRead(moveId, String(lastMessage.id));
                            // Note: markMessagesAsRead already dispatches the 'sms-read-status-changed' event
                          }
                        }
                      }}
                      sx={{
                        color: theme.palette.success.main,
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        position: 'relative',
                        zIndex: 20,
                        '&:hover': {
                          backgroundColor: theme.palette.success.light + '20',
                          color: theme.palette.success.dark,
                          borderColor: theme.palette.success.main,
                          boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                        },
                      }}
                    >
                      <BiCheck size={20} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title='Mark conversation as unread'>
                    <IconButton
                      size='small'
                      onClick={() => {
                        if (moveId && messages.length > 0) {
                          // Use the most recent message ID
                          const lastMessage = [...messages].sort((a, b) => {
                            const dateA = new Date(b.createdat || 0).getTime();
                            const dateB = new Date(a.createdat || 0).getTime();
                            return dateA - dateB;
                          })[0];
                          if (lastMessage?.id) {
                            markMoveAsUnread(moveId, String(lastMessage.id));
                            // Note: markMoveAsUnread already dispatches the 'sms-read-status-changed' event
                          }
                        }
                      }}
                      sx={{
                        color: theme.palette.text.secondary,
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        position: 'relative',
                        zIndex: 20,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.light + '15',
                          color: theme.palette.primary.main,
                          borderColor: theme.palette.primary.main,
                          boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                        },
                      }}
                    >
                      <BiEnvelope size={18} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Box className={cls.messagesContainer} ref={messagesContainerRef}>
        {Object.entries(groupedMessages).map(([date, dateMessages]) => (
          <Box key={date}>
            <Box className={cls.dateDivider}>
              <Typography variant='caption'>{date}</Typography>
            </Box>

            {dateMessages.map(message => {
              const fromDealer = isFromDealer(message);
              const statusInfo = getStatusInfo(message.status, message.error);
              const media = parseMessageMetadata(message.metadata);

              return (
                <Box
                  key={message.id}
                  className={fromDealer ? cls.messageRight : cls.messageLeft}
                  data-message-id={message.id}
                >
                  <Box className={cls.messageWrapper}>
                    <Box className={cls.messageContent}>
                      {fromDealer && (
                        <Box className={cls.messageHeader}>
                          <Typography variant='caption' className={cls.senderName}>
                            {message.createdby || 'Dealer'}
                          </Typography>
                        </Box>
                      )}

                      <Box
                        className={`
                        ${fromDealer ? cls.messageBubbleDealer : cls.messageBubbleCustomer}
                        ${fromDealer && (message.error || message.status === 'failed') ? cls.messageBubbleError : ''}
                      `.trim()}
                      >
                        {message.message_body && message.message_body !== '(Media message)' && (
                          <Typography variant='body2' className={cls.messageText}>
                            {message.message_body}
                          </Typography>
                        )}
                        <MessageImages
                          media={media}
                          imageClassName={cls.messageImage}
                          imageWrapperClassName={cls.messageImages}
                          imageSkeletonClassName={cls.imageSkeleton}
                          expandedImageClassName={cls.expandedImage}
                          expandedImageContainerClassName={cls.imageModal}
                        />
                      </Box>

                      <Box className={cls.messageFooter}>
                        <Typography variant='caption' className={cls.timestamp}>
                          {formatTime(message.createdat)}
                        </Typography>

                        {/* Status and error handling */}
                        {statusInfo.showRetry ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                            <BiError
                              size={12}
                              color={theme?.palette?.error?.main}
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleRetryMessage(message)}
                            />
                            <Typography
                              variant='caption'
                              sx={{
                                color: theme?.palette?.error?.main,
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                              onClick={() => handleRetryMessage(message)}
                            >
                              {statusInfo.label} - Tap to retry
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            variant='caption'
                            sx={{
                              color: theme?.palette?.text?.secondary,
                              ml: 1,
                              opacity: 0.7,
                            }}
                          >
                            {statusInfo.label}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Message input area */}
      <Box className={cls.inputArea}>
        <TextField
          fullWidth
          multiline
          maxRows={3}
          placeholder={!consumerPhone ? 'Customer phone not available' : 'Type a message...'}
          value={messageInput}
          onChange={e => setMessageInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending || !consumerPhone}
          size='small'
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '20px',
              backgroundColor: theme?.palette?.background?.paper,
            },
          }}
        />
        <Tooltip title={!consumerPhone ? 'Customer phone not available' : ''}>
          <span>
            <IconButton
              onClick={handleSendMessage}
              disabled={!messageInput.trim() || sending || !consumerPhone}
              color='primary'
              sx={{
                backgroundColor:
                  messageInput.trim() && !sending && consumerPhone ? theme?.palette?.primary?.main : 'transparent',
                color: messageInput.trim() && !sending && consumerPhone ? 'white' : theme?.palette?.text?.disabled,
                '&:hover': {
                  backgroundColor:
                    messageInput.trim() && !sending && consumerPhone ? theme?.palette?.primary?.dark : 'transparent',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              {sending ? <CircularProgress size={20} color='inherit' /> : <BiSend size={20} />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  chatContainer: css`
    display: flex;
    flex-direction: column;
    height: 600px;
    overflow: hidden;
  `,

  moveContext: css`
    padding: 12px 16px;
    border-bottom: 1px solid ${theme?.palette?.divider};
    background: ${theme?.palette?.grey?.[50]};
  `,

  chatHeader: css`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    border-bottom: 1px solid ${theme?.palette?.divider};
    background: ${theme?.palette?.background?.default};
  `,

  messagesContainer: css`
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background: ${theme?.palette?.grey?.[50]};
    position: relative;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: ${theme?.palette?.grey?.[400]};
      border-radius: 4px;
    }
  `,

  emptyState: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 200px;
  `,

  dateDivider: css`
    display: flex;
    justify-content: center;
    margin: 16px 0;

    & > * {
      background: ${theme?.palette?.background?.paper};
      padding: 4px 12px;
      border-radius: 12px;
      color: ${theme?.palette?.text?.secondary};
    }
  `,

  messageLeft: css`
    display: flex;
    justify-content: flex-start;
    margin-bottom: 16px;
  `,

  messageRight: css`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 16px;
  `,

  messageWrapper: css`
    display: flex;
    gap: 8px;
    max-width: 90%;
  `,

  avatar: css`
    flex-shrink: 0;
  `,

  messageContent: css`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  `,

  messageHeader: css`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 4px;
  `,

  senderName: css`
    font-weight: 600;
    color: ${theme?.palette?.text?.primary};
  `,

  timestamp: css`
    color: ${theme?.palette?.text?.secondary};
  `,

  messageBubbleCustomer: css`
    padding: 8px 12px;
    background: ${theme?.palette?.background?.paper};
    border-radius: 12px;
    border-top-left-radius: 4px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  `,

  messageBubbleDealer: css`
    padding: 8px 12px;
    background: linear-gradient(135deg, #4068a0 0%, #335380 100%);
    color: white;
    border-radius: 12px;
    border-top-right-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  `,

  messageBubbleError: css`
    background: linear-gradient(135deg, #e57373 0%, #d32f2f 100%) !important;
    border: 1px solid ${theme?.palette?.error?.main};
    opacity: 0.8;
  `,

  messageText: css`
    white-space: pre-wrap;
    word-break: break-word;
  `,

  messageFooter: css`
    display: flex;
    align-items: center;
    padding: 2px 4px;
  `,

  inputArea: css`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid ${theme?.palette?.divider};
    background: ${theme?.palette?.background?.paper};
  `,

  // Image display styles
  messageImages: css`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  `,

  imageWrapper: css`
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    background: ${theme?.palette?.grey?.[100]};
  `,

  imageSkeleton: css`
    width: 200px;
    height: 150px;
    border-radius: 8px;
  `,

  messageImage: css`
    max-width: 200px;
    max-height: 200px;
    width: auto;
    height: auto;
    display: block;
    border-radius: 8px;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.95;
    }
  `,

  expandButton: css`
    position: absolute;
    top: 4px;
    right: 4px;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(4px);

    &:hover {
      background: rgba(255, 255, 255, 1);
    }
  `,

  imageModal: css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    cursor: pointer;
    padding: 20px;
  `,

  expandedImage: css`
    max-width: 90vw;
    max-height: 90vh;
    object-fit: contain;
  `,

  closeModal: css`
    position: absolute;
    top: 20px;
    right: 20px;
    color: white;
    background: rgba(0, 0, 0, 0.5);

    &:hover {
      background: rgba(0, 0, 0, 0.7);
    }
  `,
});

// Direct component export - global subscription handles updates
const MoveChatInterface = ({ moveId, consumerPickup }: MoveChatInterfaceProps) => {
  // Global subscription provider in Layout handles all real-time updates
  return <MoveChatInterfaceContent moveId={moveId} consumerPickup={consumerPickup} />;
};

// EXPORT ---------------------------------------------------------------- //

export default MoveChatInterface;
